/**
 * Public types and shared helpers for InfrastructureMapCanvas.
 *
 * The implementation is split across:
 *   - this file: public types, adjacency, parent-child map, links/orphans, focal zoom
 *   - `./infrastructure-map-positions.helpers.ts`: collapsed-parent and bus-mode positions, nodes-to-render
 *   - `./infrastructure-map-topology.helpers.ts`: bus and tree topology data
 *
 * Splitting follows the lint ceiling (500 lines per file) and groups concerns
 * by stage of the layout pipeline.
 */

import type {
  CanvasPosition,
  EndpointDisplayInfo,
  LinkStatus,
  TileSize,
} from '../../types/infrastructure-map'
import type { EndpointRelationship } from '../../types/case-container'

export interface RenderableNode {
  endpointId: string
  displayType: 'tile' | 'dot'
  position: CanvasPosition
  tileSize: TileSize
}

export interface BusTopologyNode {
  endpointId: string
  position: CanvasPosition
  isHub: boolean
  childCount: number
  isCollapsed: boolean
}

export interface BusTopologyGroup {
  lanGroupId: number
  busY: number
  hubId: string
  collapsedCount: number
  collapsedEndpointIds: string[]
  nodes: BusTopologyNode[]
}

export interface TreeTopologyNode {
  endpointId: string
  position: CanvasPosition
  parentId: string | null
  hasChildren: boolean
  childCount: number
  isCollapsed: boolean
}

export interface TreeTopologyGroup {
  lanGroupId: number
  nodes: TreeTopologyNode[]
}

export interface RenderableLink {
  id: string
  sourcePosition: CanvasPosition
  targetPosition: CanvasPosition
  status: LinkStatus
  hasOpenQuestion: boolean
}

export interface OrphanEndpointSummary {
  id: string
  hostname: string
  ipAddress: string
  deviceType: string
}

export interface ZoomViewport {
  x: number
  y: number
  zoom: number
  width: number
  height: number
}

const CONFIRMED_STATUS = 'confirmed'

export function buildConfirmedAdjacency(
  relationships: EndpointRelationship[]
): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>()
  for (const rel of relationships) {
    if (rel.status !== CONFIRMED_STATUS) continue
    ensureNeighborSet(adjacency, rel.sourceEndpointId).add(rel.targetEndpointId)
    ensureNeighborSet(adjacency, rel.targetEndpointId).add(rel.sourceEndpointId)
  }
  return adjacency
}

export function ensureNeighborSet(adjacency: Map<string, Set<string>>, id: string): Set<string> {
  let set = adjacency.get(id)
  if (!set) {
    set = new Set()
    adjacency.set(id, set)
  }
  return set
}

export function findConnectedComponents(adjacency: Map<string, Set<string>>): string[][] {
  const visited = new Set<string>()
  const components: string[][] = []
  for (const [startId] of adjacency) {
    if (visited.has(startId)) continue
    components.push(bfsComponent(startId, adjacency, visited))
  }
  return components.filter((component) => component.length > 0)
}

function bfsComponent(
  startId: string,
  adjacency: Map<string, Set<string>>,
  visited: Set<string>
): string[] {
  const component: string[] = []
  const queue = [startId]
  visited.add(startId)
  while (queue.length > 0) {
    const nodeId = queue.shift()
    if (nodeId === undefined) break
    component.push(nodeId)
    for (const neighborId of adjacency.get(nodeId) ?? new Set()) {
      if (!visited.has(neighborId)) {
        visited.add(neighborId)
        queue.push(neighborId)
      }
    }
  }
  return component
}

export function buildParentChildMap(relationships: EndpointRelationship[]): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const rel of relationships) {
    if (rel.status !== CONFIRMED_STATUS) continue
    const existing = map.get(rel.sourceEndpointId) ?? []
    existing.push(rel.targetEndpointId)
    map.set(rel.sourceEndpointId, existing)
  }
  return map
}

const UNKNOWN_DEVICE = 'unknown'

interface OrphanEndpoint {
  id: string
  hostname: string
  ipAddresses?: string[] | null
  deviceType?: string | null
}

export function buildOrphanEndpoints(
  orphanIds: string[],
  endpointById: Map<string, OrphanEndpoint>
): OrphanEndpointSummary[] {
  const result: OrphanEndpointSummary[] = []
  for (const id of orphanIds) {
    const endpoint = endpointById.get(id)
    if (!endpoint) continue
    result.push({
      id: endpoint.id,
      hostname: endpoint.hostname,
      ipAddress: endpoint.ipAddresses?.[0] ?? '',
      deviceType: endpoint.deviceType ?? UNKNOWN_DEVICE,
    })
  }
  return result
}

const DEFAULT_LINK_STATUS: LinkStatus = 'confirmed'
const OPEN_QUESTION_STATUS: LinkStatus = 'open-question'

export function buildLinksToRender(
  relationships: EndpointRelationship[],
  getEffectivePosition: (endpointId: string) => CanvasPosition | null
): RenderableLink[] {
  const links: RenderableLink[] = []
  for (const rel of relationships) {
    const sourcePosition = getEffectivePosition(rel.sourceEndpointId)
    const targetPosition = getEffectivePosition(rel.targetEndpointId)
    if (!sourcePosition || !targetPosition) continue
    links.push({
      id: rel.id,
      sourcePosition,
      targetPosition,
      status: (rel.status as LinkStatus | null) ?? DEFAULT_LINK_STATUS,
      hasOpenQuestion: rel.status === OPEN_QUESTION_STATUS,
    })
  }
  return links
}

export interface FocalZoomContext {
  prev: ZoomViewport
  newZoom: number
  selectedEndpointIds: Set<string>
  displayInfo: Map<string, EndpointDisplayInfo>
}

export function computeFocalZoom(context: FocalZoomContext): ZoomViewport {
  const { prev, newZoom, selectedEndpointIds, displayInfo } = context
  const { focalX, focalY } = resolveFocalPoint(prev, selectedEndpointIds, displayInfo)
  const zoomDelta = prev.zoom - newZoom
  const newX = prev.x + focalX * zoomDelta
  const newY = prev.y + focalY * zoomDelta
  return { ...prev, x: newX, y: newY, zoom: newZoom }
}

const HALF = 2

function resolveFocalPoint(
  prev: ZoomViewport,
  selectedEndpointIds: Set<string>,
  displayInfo: Map<string, EndpointDisplayInfo>
): { focalX: number; focalY: number } {
  const screenCentre = {
    focalX: (prev.width / HALF - prev.x) / prev.zoom,
    focalY: (prev.height / HALF - prev.y) / prev.zoom,
  }
  const firstSelectedId = selectedEndpointIds.size > 0 ? [...selectedEndpointIds][0] : null
  if (!firstSelectedId) return screenCentre
  const info = displayInfo.get(firstSelectedId)
  if (!info?.position) return screenCentre
  return { focalX: info.position.x, focalY: info.position.y }
}

export {
  computeBusCompressedPositions,
  buildNodesToRender,
  computeCollapsedPositions,
} from './infrastructure-map-positions.helpers'

export { buildBusTopologyData, buildTreeTopologyData } from './infrastructure-map-topology.helpers'
